import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, CalendarClock, ChevronRight, Clock3, Inbox } from "lucide-react";
import api from "@/lib/api";
import { Panel } from "@/components/client/HomePanels";

const KIND = {
  OFFER_AWAITING_DECISION: { label: "Offer to review", tone: "bg-jigsaw-orange/10 text-jigsaw-orangeDark" },
  STAY_ENDING_SOON: { label: "Ending soon", tone: "bg-jigsaw-surface text-jigsaw-navy" },
  CHANGE_AWAITING_JIGSAW: { label: "With Jigsaw", tone: "bg-sky-100 text-sky-800" },
};

function Item({ item, onOpen }) {
  const k = KIND[item.kind] || { label: item.kind, tone: "bg-jigsaw-border text-jigsaw-navy" };
  return (
    <li>
      <button type="button" data-testid={`attention-item-${item.kind.toLowerCase()}`} onClick={() => onOpen(item.link)}
        className="group flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-jigsaw-surface">
        <span className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${k.tone}`}>{k.label}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-jigsaw-navy">{item.title}</span>
          {item.detail && <span className="block truncate text-[12px] text-jigsaw-muted">{item.detail}</span>}
        </span>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-jigsaw-muted transition-transform group-hover:translate-x-0.5" />
      </button>
    </li>
  );
}

export function AttentionList({ className = "" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/client/attention").then((r) => setData(r.data)).catch(() => setError(true)); }, []);
  const yours = (data?.items || []).filter((i) => i.owner === "YOU");
  const jigsaw = (data?.items || []).filter((i) => i.owner === "JIGSAW");
  const info = (data?.items || []).filter((i) => i.owner === "INFO");
  return (
    <Panel title="Attention" testId="client-attention-panel" className={className}
      action={data && <span data-testid="client-attention-counts" className="text-[12px] text-jigsaw-muted">{data.your_action} for you · {data.awaiting_jigsaw} with Jigsaw · {data.ending_soon} ending soon</span>}>
      {!data && !error && <p data-testid="client-attention-loading" className="px-2 py-3 text-[13px] text-jigsaw-muted">Checking what needs attention…</p>}
      {error && <p data-testid="client-attention-error" className="px-2 py-3 text-[13px] text-red-700">Attention items could not be loaded right now.</p>}
      {data && data.items.length === 0 && (
        <div data-testid="client-attention-empty" className="flex items-center gap-3 px-2 py-4 text-[13px] text-jigsaw-muted"><Inbox className="h-5 w-5" />Nothing to show. Offers to review, change requests with Jigsaw and stays ending within seven days will appear here.</div>
      )}
      {yours.length > 0 && (
        <section data-testid="client-attention-yours" className="mb-2">
          <h3 className="flex items-center gap-1.5 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-jigsaw-orangeDark"><BellRing className="h-3.5 w-3.5" />Your action required</h3>
          <ul className="divide-y divide-jigsaw-border/60">{yours.map((i) => <Item key={i.entity_id} item={i} onOpen={navigate} />)}</ul>
        </section>
      )}
      {jigsaw.length > 0 && (
        <section data-testid="client-attention-jigsaw" className="mb-2">
          <h3 className="flex items-center gap-1.5 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-jigsaw-muted"><Clock3 className="h-3.5 w-3.5" />Awaiting Jigsaw</h3>
          <ul className="divide-y divide-jigsaw-border/60">{jigsaw.map((i) => <Item key={i.entity_id} item={i} onOpen={navigate} />)}</ul>
        </section>
      )}
      {info.length > 0 && (
        <section data-testid="client-attention-ending-soon">
          <h3 className="flex items-center gap-1.5 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-jigsaw-muted"><CalendarClock className="h-3.5 w-3.5" />Ending soon</h3>
          <ul className="divide-y divide-jigsaw-border/60">{info.map((i) => <Item key={i.entity_id} item={i} onOpen={navigate} />)}</ul>
        </section>
      )}
      {data && <p className="px-2 pt-2 text-[11px] text-jigsaw-muted">Stays ending through {data.boundary.stays_ending_through} (UK calendar days).</p>}
    </Panel>
  );
}
